import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import { Trash2 } from 'react-feather'
import { formatDistanceToNow } from 'date-fns'
import { ResearcherEventAttentionTask } from '@/types'
import { useDeleteAttentionTask } from '@/hooks/api/useDeleteAttentionTask'
import { useMutationWrapper } from '@/hooks'

type AttentionTaskListProps = {
  tasks: ResearcherEventAttentionTask[]
  eventId: number
}

export const AttentionTaskList = ({
  tasks,
  eventId,
}: AttentionTaskListProps): JSX.Element => {
  const { t } = useTranslation(['monitoring', 'common'])
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null)

  const { mutateAsync: deleteTask, isLoading: isDeleting } = useMutationWrapper(
    () => useDeleteAttentionTask({ eventId })
  )

  const handleDeleteClick = (taskId: number) => {
    setTaskToDelete(taskId)
  }

  const handleConfirmDelete = async () => {
    if (taskToDelete !== null) {
      await deleteTask(taskToDelete, {
        successMessageKey: 'monitoring:notifications.attentionTaskDeleted',
      })
      setTaskToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setTaskToDelete(null)
  }

  if (tasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('monitoring:noAttentionTasks')}
      </Typography>
    )
  }

  return (
    <>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {tasks.map((task) => (
          <ListItem
            key={task.id}
            alignItems="flex-start"
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleDeleteClick(task.id)}
              >
                <Trash2 size={18} />
              </IconButton>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: 2,
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography
                    variant="body1"
                    component="span"
                    fontWeight="medium"
                  >
                    {task.message}
                  </Typography>
                </Box>
              }
              secondary={
                <Box>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Typography variant="caption" color="text.secondary" mr={1}>
                      {t('monitoring:createdBy')}: {task.created_by.first_name}{' '}
                      {task.created_by.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(task.created_at), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Chip
                      label={
                        task.is_completed
                          ? t('monitoring:completed')
                          : t('monitoring:pending')
                      }
                      size="small"
                      color={task.is_completed ? 'success' : 'warning'}
                      sx={{ height: 24 }}
                    />
                    {task.is_completed && task.completed_by && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        ml={1}
                      >
                        {t('monitoring:completedBy')}:{' '}
                        {task.completed_by.first_name}{' '}
                        {task.completed_by.last_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={taskToDelete !== null} onClose={handleCancelDelete}>
        <DialogTitle>
          {t('monitoring:titles.deleteAttentionTaskTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('monitoring:deleteAttentionTaskConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
